"use client";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchBankDetails,
  createBankDetails,
  updateBankDetails,
  selectBankDetailsByCountry,
} from "../../../redux/features/bankDetailSlice";

const standardBankFields = [
  { name: "bankName", label: "Bank Name", type: "text", placeholder: "Enter bank name", maxLength: 100 },
  { name: "accountTitle", label: "Account Title", type: "text", placeholder: "Enter account title", maxLength: 100 },
  { name: "accountNumber", label: "Account Number", type: "text", placeholder: "Enter account number", maxLength: 20 },
  { name: "iban", label: "IBAN", type: "text", placeholder: "Enter IBAN", maxLength: 34 },
  { name: "payId", label: "Pay Id", type: "number", placeholder: "Enter Pay Id", maxLength: 10 },
  { name: "branchCode", label: "Branch Code", type: "text", placeholder: "Enter Branch Code", maxLength: 20 },
];

const BankDetailsForm = ({ selectedCountry }) => {
  const [bankDetails, setBankDetails] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [validationError, setValidationError] = useState("");

  const dispatch = useDispatch();
  const { loading, error, success } = useSelector((state) => state.bankDetails);
  const existingBankDetails = useSelector((state) =>
    selectBankDetailsByCountry(state, selectedCountry)
  );

  useEffect(() => {
    if (!existingBankDetails) dispatch(fetchBankDetails());  
  }, [dispatch]);

  useEffect(() => {
    setValidationError("");
    setSuccessMessage("");

    if (selectedCountry && existingBankDetails) {
      console.log(existingBankDetails.payId)
      setIsUpdate(true);
      setBankDetails({
        bankName: existingBankDetails.bankName || "",
        accountTitle: existingBankDetails.accountTitle || "",
        accountNumber: existingBankDetails.accountNumber || "",
        iban: existingBankDetails.iban || "",
        payId: existingBankDetails.payId || "",
        branchCode: existingBankDetails.branchCode || "",
      });
    } else {
      setIsUpdate(false);
      setBankDetails({
        bankName: "",
        accountTitle: "",
        accountNumber: "",
        iban: "",
        payId: "",
        branchCode: "",
      });
    }
  }, [selectedCountry, existingBankDetails]);

  useEffect(() => {
    if (success) {
      const message = isUpdate
        ? "Bank details updated successfully!"
        : "Bank details created successfully!";

      setSuccessMessage(message);

      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [success, isUpdate]);

  const sanitizeBankText = (fieldName, value) => {
    if (fieldName === "bankName" || fieldName === "accountTitle") {
      return value.replace(/[^a-zA-Z\s]/g, "");
    }

    if (fieldName === "accountNumber" || fieldName === "branchCode") {
      return value.replace(/\D/g, "");
    }

    return value;
  };

  const isRequiredField = (fieldName) =>
    fieldName !== "payId" || selectedCountry === "Australia";

  const renderFieldLabel = (field) => (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {field.label}
      {isRequiredField(field.name) && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  const handleBankDetailsChange = (e, maxLength) => {
    const { name, value } = e.target;

    let newValue = value;

    if (maxLength) {
      newValue = value.slice(0, maxLength);
    }

    newValue = sanitizeBankText(name, newValue);
    
    setBankDetails((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    const requiredFields = standardBankFields
      .filter((field) => isRequiredField(field.name))
      .map((field) => field.name);
    const missingFields = requiredFields.filter((field) => !bankDetails[field]);

    if (missingFields.length > 0) {
      setValidationError("Please fill in all required fields");
      return;
    }

    const bankDetailsData = {
      ...bankDetails,
      country: selectedCountry,
    };

    if (isUpdate) {
      dispatch(updateBankDetails(bankDetailsData));
    } else {
      await dispatch(createBankDetails(bankDetailsData));
      setIsUpdate(true);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-[500px]">
      <h2 className="text-xl font-medium mb-4">Bank Details</h2>

      {successMessage && (
        <AnimatePresence>
          <motion.div
            className="border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 bg-green-50"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <span className="block sm:inline font-medium">
              {successMessage}
            </span>
          </motion.div>
        </AnimatePresence>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {validationError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{validationError}</span>
        </div>
      )}

      <div className="space-y-4 text-sm md:text-base">
        {standardBankFields
          .filter((field) => {
            // Hide payId unless the selectedCountry is Australia
            if (field.name === "payId" && selectedCountry !== "Australia") {
              return false;
            }
            return true;
          })
          .map((field) => (
            <div key={field.name}>
              {renderFieldLabel(field)}
              <input
                type={field.type}
                name={field.name}
                value={bankDetails[field.name] || ""}
                onChange={(e) => handleBankDetailsChange(e, field.maxLength)}
                placeholder={field.placeholder}
                maxLength={field.maxLength}
                inputMode={field.name === "accountNumber" || field.name === "branchCode" ? "numeric" : undefined}
                pattern={field.name === "accountNumber" || field.name === "branchCode" ? "[0-9]*" : undefined}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required={isRequiredField(field.name)}
              />
              {field.maxLength ? (
                <p className="mt-1 text-xs text-gray-500">Max {field.maxLength} characters</p>
              ) : null}
            </div>
          ))}

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-[#005787] text-white py-2 px-4 rounded-full hover:bg-[#004a70] transition-colors flex justify-center items-center ${loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
          >
            {loading ? (
              <span className="loader"></span>
            ) : isUpdate ? (
              "Update Bank Details"
            ) : (
              "Save Bank Details"
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default BankDetailsForm;